import { Modal, Box, Typography, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { useState, useEffect } from 'react';
import LockerSpotEditModal from './LockerSpotEditModal';
import ConfirmModal from '../../../../common/modal/ConfirmModal';
import { deleteLockerArea } from '../../../../../api/ticket';
import { useSelector } from 'react-redux';

// Styled Components
const ModalContainer = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '420px',
  backgroundColor: '#fff',
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
  borderRadius: '16px',
  overflow: 'hidden',
});

const ModalHeader = styled(Box)({
  height: '56px',
  backgroundColor: '#262626',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 24px',
});

const HeaderTitle = styled(Typography)({
  color: '#fff',
  fontSize: '18px',
  fontWeight: 600,
});

const CloseButton = styled(Box)({
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': {
    color: '#fff',
    width: '24px',
    height: '24px',
  },
});

const ModalContent = styled(Box)({
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  maxHeight: '400px',
  overflowY: 'auto',
});

const AreaItem = styled(Box)({
  padding: '12px 16px',
  border: '1px solid #E0E0E0',
  borderRadius: '8px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const AreaName = styled(Typography)({
  fontSize: '15px',
  fontWeight: 500,
  color: '#333',
});

const ButtonGroup = styled(Box)({
  display: 'flex',
  gap: '8px',
});

const EditButton = styled(Box)({
  width: '48px',
  height: '28px',
  borderRadius: '6px',
  border: '1px solid #4A6A94',
  fontSize: '13px',
  fontWeight: 500,
  color: '#4A6A94',
  textAlign: 'center',
  lineHeight: '26px',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: 'rgba(85, 201, 130, 0.1)',
  },
});

const DeleteButton = styled(Box)({
  width: '48px',
  height: '28px',
  borderRadius: '6px',
  border: '1px solid #EE735A',
  fontSize: '13px',
  fontWeight: 500,
  color: '#EE735A',
  textAlign: 'center',
  lineHeight: '26px',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: 'rgba(238, 115, 90, 0.1)',
  },
});

const EmptyText = styled(Typography)({
  fontSize: '14px',
  fontWeight: 400,
  color: '#999',
  textAlign: 'center',
  padding: '20px 0',
});

const AddAreaButton = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  cursor: 'pointer',
  marginTop: '8px',
  '& svg': {
    color: '#1F3A5F',
    fontSize: '20px',
  },
});

const AddAreaText = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#1F3A5F',
});

const ModalFooter = styled(Box)({
  padding: '16px 24px 24px 24px',
  display: 'flex',
  gap: '12px',
});

const SaveButton = styled(Button)({
  flex: 2,
  height: '48px',
  borderRadius: '8px',
  backgroundColor: '#1F3A5F',
  fontSize: '16px',
  fontWeight: 500,
  color: '#fff',
  '&:hover': {
    backgroundColor: '#172C48',
  },
});

// Main Component
const LockerSpotSettingModal = ({ branchIdData, open, handleClose, lockerAreas, onSuccess }) => {
  const { t } = useTranslation();
  const user = useSelector(state => state.user.user);

  const [localLockerAreas, setLocalLockerAreas] = useState(lockerAreas || []);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    setLocalLockerAreas(lockerAreas || []);
  }, [lockerAreas]);

  const handleAddClick = () => {
    setEditData(null);
    setIsEditMode(false);
    setOpenEditModal(true);
  };

  const handleEditClick = area => {
    setEditData(area);
    setIsEditMode(true);
    setOpenEditModal(true);
  };

  // 삭제 요청 모달
  const handleDeleteClick = area => {
    setDeleteTarget(area);
    setOpenDeleteConfirm(true);
  };

  // 삭제 모달에서 버튼 누르면 실행되는 함수
  const handleDeleteConfirm = async () => {
    if (deleteTarget) {
      try {
        await deleteLockerArea(deleteTarget?.branchId, deleteTarget?.id, user?.token);
        setOpenDeleteConfirm(false);
        setDeleteTarget(null);
        onSuccess?.();
      } catch (error) {
        console.log('handleDeleteConfirm 함수 에러 : ' + error);
      }
    } else {
      console.log('삭제할 데이터가 없습니다.');
      setOpenDeleteConfirm(false);
    }
  };

  const handleEditSuccess = () => {
    onSuccess?.();
  };

  return (
    <>
      <Modal open={open} onClose={handleClose} disableEscapeKeyDown>
        <ModalContainer>
          {/* Header */}
          <ModalHeader>
            <HeaderTitle>{t('라커 구역 관리')}</HeaderTitle>
            <CloseButton onClick={handleClose}>
              <CloseIcon />
            </CloseButton>
          </ModalHeader>

          {/* Content */}
          <ModalContent>
            {localLockerAreas && localLockerAreas.length > 0 ? (
              localLockerAreas.map((area, index) => (
                <AreaItem key={area.id || index}>
                  <AreaName>{area.lockerAreaName}</AreaName>
                  <ButtonGroup>
                    <EditButton onClick={() => handleEditClick(area)}>{t('수정')}</EditButton>
                    <DeleteButton onClick={() => handleDeleteClick(area)}>{t('삭제')}</DeleteButton>
                  </ButtonGroup>
                </AreaItem>
              ))
            ) : (
              <EmptyText>{t('등록된 구역이 없습니다.')}</EmptyText>
            )}

            {/* Add Area Button */}
            <AddAreaButton onClick={handleAddClick}>
              <AddIcon />
              <AddAreaText>{t('라커 구역 추가')}</AddAreaText>
            </AddAreaButton>
          </ModalContent>

          {/* Footer */}
          <ModalFooter>
            <SaveButton onClick={handleClose}>{t('확인')}</SaveButton>
          </ModalFooter>
        </ModalContainer>
      </Modal>

      {/* Edit/Add Modal */}
      <LockerSpotEditModal
        branchIdData={branchIdData}
        open={openEditModal}
        handleClose={() => setOpenEditModal(false)}
        editData={editData}
        onSuccess={handleEditSuccess}
        isEditMode={isEditMode}
      />

      {/* Delete Confirm Modal */}
      <ConfirmModal
        open={openDeleteConfirm}
        onClose={() => {
          setOpenDeleteConfirm(false);
          setDeleteTarget(null);
        }}
        onConfirm={handleDeleteConfirm}
        highlightText="삭제"
        highlightColor="#F44336"
        cancelText="취소"
        confirmText="삭제"
        confirmColor="#F44336"
      />
    </>
  );
};

export default LockerSpotSettingModal;
